import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { paths } from '../../../common/constants/pathVariables.js';
import { ShoppingBasketCoCoScope } from '../../../common/enums.js';
import { parseBasketJson, useShoppingBasketCoCo } from '../../../common/hooks/useShoppingBasketCoCo.js';
import { ScrollToTop } from '../../../common/utils/browserUtils.js';
import { GridNarrow } from '../../../components/Grid/Grid.js';
import { getUserAccounts } from '../../../components/Header/dynamic/headerFunctions.js';
import { LoginAndRegistration } from '../../../components/LoginAndRegistration/LoginAndRegistration.js';
import { ShoppingBasketCoCo } from '../../../components/ShoppingBasket/ShoppingBasketCoCo.js';
import {
  applyQuantityChangeToBasket,
  updateAddonGroupSelectionInBasket,
  updateAddonInBasket,
  updateProductOfferingRuntimeCharacteristicInBasket,
} from '../../../components/ShoppingBasket/shoppingBasketHelpersCoCo.js';
import { useAuth } from '../AuthProvider.js';
import { useSessionData } from '../SessionDataProvider.js';
import type { ProductOfferingCartItems, ShoppingBasketType } from '../../../common/types/shoppingBasketCoCo.js';

export const ShoppingBasketPathCoCo = ({ isEmployee = false }: { isEmployee?: boolean } = {}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const loginInProgress = location.state?.loginInProgress === true;
  const { isAuthenticated, authenticatedUser, inMigrationPhase } = useAuth();

  const { shoppingBasket, setShoppingBasket } = useShoppingBasketCoCo(
    isEmployee ? ShoppingBasketCoCoScope.EMPLOYEE : ShoppingBasketCoCoScope.ADMIN
  );
  const shoppingCartJson: ShoppingBasketType = parseBasketJson(shoppingBasket);

  const userAccounts = getUserAccounts(authenticatedUser) || [];

  const companyInfo = useSessionData().companyInfo;

  const isFetching = authenticatedUser?.isFetching;
  const isCompanyNameEmpty = companyInfo?.companyName && companyInfo?.companyName?.length === 0;

  const isLoading = isFetching || (isFetching === false && isCompanyNameEmpty === undefined);

  const updateCartItemQuantity = (newQty: number, groupItems: ProductOfferingCartItems[]) => {
    const itemCountChange = newQty - groupItems.length;
    if (itemCountChange === 0) {
      return;
    }
    setShoppingBasket(JSON.stringify(applyQuantityChangeToBasket(shoppingCartJson, newQty, groupItems)));
  };

  const updateCartItemAddonSelection = (addonId: string, groupCartItemIds: string[]) => {
    setShoppingBasket(JSON.stringify(updateAddonInBasket(shoppingCartJson, addonId, groupCartItemIds)));
  };

  const updateCartItemAddonGroupSelection = (
    groupCartItemIds: string[],
    groupId: string,
    selectedOfferingIds: string[]
  ) => {
    setShoppingBasket(
      JSON.stringify(
        updateAddonGroupSelectionInBasket(shoppingCartJson, groupCartItemIds, groupId, selectedOfferingIds)
      )
    );
  };

  const updateCartItemRuntimeCharacteristic = (groupCartItemIds: string[], selectedValue: string) => {
    setShoppingBasket(
      JSON.stringify(
        updateProductOfferingRuntimeCharacteristicInBasket(shoppingCartJson, groupCartItemIds, selectedValue)
      )
    );
  };

  useEffect(() => {
    if (!isEmployee && !isLoading && loginInProgress && userAccounts.length === 1) {
      navigate(paths.SHOPPING_BASKET, { replace: true });
    }
  }, [isEmployee, loginInProgress, isLoading, navigate, userAccounts.length]);

  if (!isEmployee && !isAuthenticated && loginInProgress) {
    if (inMigrationPhase) {
      return (
        <>
          <ScrollToTop />
          <LoginAndRegistration includeAdmins />
        </>
      );
    }
    return (
      <GridNarrow>
        <ScrollToTop />
        <LoginAndRegistration />
      </GridNarrow>
    );
  }

  return (
    <div>
      <ShoppingBasketCoCo
        isEmployee={isEmployee}
        userAccounts={isEmployee ? [] : userAccounts}
        shoppingBasket={shoppingCartJson}
        loggedIn={isAuthenticated}
        loginInProgress={!isEmployee && loginInProgress}
        updateCartItemQuantity={updateCartItemQuantity}
        updateCartItemAddonSelection={updateCartItemAddonSelection}
        updateCartItemAddonGroupSelection={updateCartItemAddonGroupSelection}
        updateCartItemRuntimeCharacteristic={updateCartItemRuntimeCharacteristic}
        updateBasket={setShoppingBasket}
      />
    </div>
  );
};
